<template>
  <vx-card class="mt-base mb-5" title="3. Illinois Agility Test">
    <p class="mb-5">
      This Illinois Agility Test is designed to test an applicant's speed and athletic agility. This
      is a timed test that requires you to push yourself to run your maximum speed while negotiating
      your way through a course that contains several traffic cones.
    </p>
    <p class="mb-5">
      The image below gives a brief account of how the test is performed and how the course is laid
      out.
    </p>

    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
        <img
          src="@/assets/images/experiences/policing/info3.png"
          alt="Illinois Agility Test. Goal: you are required to run the full course in 20 seconds or less. How: set up a course approximately 9.14m by 4.16m in size. Use household items such as plastic bottles as markers to replace cones. Start by lying flat on the floor, hands beside your shoulders, and run the course without touching any of the markers. Why: to test your speed and agility. Tip: practise your starts so you can pop up with speed from the floor position and transition into a sprint quickly, and avoid hitting the markers as you weave through the course. Agility course set-up diagram: a rectangle 9.14m long and 4.16m wide. The start line is at the top left and the finish line at the top right; 'touch the line' markers are at the bottom left and bottom right. Four cones are spaced evenly in a line down the centre. The route runs from the start straight down to touch the bottom-left line, diagonally back up to the top of the centre cones, weaves down through the four cones and back up, then runs diagonally down to touch the bottom-right line and straight up to the finish."
          class="mx-auto img-responsive"
        />
      </div>
    </div>

    <p class="mb-5">
      To practice this test at home, you’ll need to find an area at approximately 10m x 5m to mark
      out the course as above. You’ll need:
    </p>
    <ul class="default-list">
      <li>A measuring tape to measure the distances accurately</li>
      <li>Chalk to mark out the start and finish lines, along with the touchlines</li>
      <li>4 traffic cones (improvise if traffic cones aren’t available)</li>
      <li>A helper to time you.</li>
    </ul>

    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5 text-center">
        <responsive-iframe-youtube :src="videos[1]" :posterimg="videoTrigger1" />
      </div>
    </div>

    <p class="mb-5">To begin the test:</p>
    <ul class="default-list">
      <li>Lie on your stomach with your head on the start line</li>
      <li>
        Place your hands by your shoulders with arms flat on the ground and both legs extended
        behind the line
      </li>
      <li>
        When you’re ready to go, get to your feet as quickly as possible and sprint to the top of
        the course and touch the line with one foot
      </li>
      <li>Turn, and run to the first cone at the bottom end of the course</li>
      <li>
        Turn and weave in and out of the row of cones and back again towards the bottom of the
        course
      </li>
      <li>
        Finally, sprint once more to the line at the top of the course and touch it with your foot
      </li>
      <li>Sprint back to the finish line.</li>
    </ul>

    <p class="mb-5">
      If you fail to touch a line, miss, knock, or touch a cone, it will be considered a failed
      attempt.
    </p>
    <p class="mb-5">
      To pass the minimum requirement, you must complete the test in 20 seconds or less and you are
      allowed only 2 failed attempts in the actual test.
    </p>
    <p class="mb-5">
      Over your work experience week, practice the Illinois Agility Test at least once daily. Try to
      improve your time a little each day.
    </p>

    <h4 class="mb-5">
      <strong><u>Illinois Agility Test Progressive Training Programme</u></strong>
    </h4>
    <p class="mb-5">
      The Illinois Agility Test can be broken down into 3 distinct aspects. These aspects can be
      isolated and trained separately to improve your overall performance in the test as a whole.
    </p>
    <p class="mb-5">These 3 aspects are:</p>
    <ul class="default-list">
      <li>The Take-off</li>
      <li>Weaving through obstacles</li>
      <li>The Sprint</li>
    </ul>

    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5 text-center">
        <responsive-iframe-youtube :src="videos[2]" :posterimg="videoTrigger2" />
      </div>
    </div>

    <h4 class="mb-5">
      <strong>Top Tips to Improve Your Illinois Agility Test Performance</strong>
    </h4>
    <ul class="default-list">
      <li>Aim to get to your feet as quickly as possible - practice this movement.</li>
      <li>Practice sprinting to improve your straight runs</li>
      <li>Go 100% all the time - this test is a sprint, not a marathon! Don’t conserve energy</li>
      <li>Make sure you touch the lines with your foot at the top and bottom of the course</li>
      <li>Stay well clear of the cones when weaving</li>
      <li>Don’t slow down as you come towards the end of the course.</li>
    </ul>
    <p class="mb-5">Remember to record your results on the Physical Training Record Sheet.</p>

    <div class="vx-row mb-6">
      <div class="vx-col w-full">
        <vs-checkbox v-model="confirmed" name="confirmed"
          >I have read all the information on this page
        </vs-checkbox>
      </div>
    </div>

    <div class="vx-row">
      <div class="vx-col w-full">
        <button class="btn btn-secondary btn-size-medium mb-1" @click="$router.back()">Back</button>
      </div>
    </div>
  </vx-card>
</template>

<script>
export default {
  data() {
    return {
      videoTrigger1: require('@/assets/images/experiences/policing/agility_test.jpg'),
      videoTrigger2: require('@/assets/images/experiences/policing/agility_test_prepare.jpg'),
      videos: {
        1: 'https://www.youtube.com/embed/pMmZ_cXiElw',
        2: 'https://www.youtube.com/embed/I3OCC9nNUTc',
      },
      confirmed: false,
    };
  },
};
</script>
